import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import {
  LocalStorageService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import {
  ExclamationIcon,
  LOCAL_STORAGE_KEYS,
  LOGIN,
  ROUTE_CONSTS,
  RegexEnum,
} from '@g99-product-web-ui/shared-util-lib';
import { take } from 'rxjs';

import {
  IUserBusinessInfo,
  IUserCredentials,
} from '../../interface/ISignIn.interface';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'g99-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  standalone: false,
})
export class SignInComponent implements OnInit {
  protected labels = LOGIN.SIGN_IN.LABELS;
  protected placeholders = LOGIN.SIGN_IN.PLACEHOLDERS;
  protected errors = LOGIN.SIGN_IN.ERRORS;
  protected toastErrorMessages = LOGIN.SIGN_IN.TOAST_MESSAGES_ERRORS;
  protected signInForm: FormGroup;
  protected submitted = false;
  protected loading = false;
  protected errorMessage = '';
  protected tenantId!: number;
  protected userData!: IUserBusinessInfo[];
  protected showBusinessSelectionModal = false;
  protected showPassword = false;
  protected isLoading = false;
  protected isUnauthorized = false;
  protected exclamationSvg = ExclamationIcon;
  protected errorMessages = {
    email: {
      required: this.errors.INAVLID_EMAIL_REQUIRED,
      pattern: this.errors.INVALID_EMAIL_PATTERN,
    },
    password: {
      required: this.errors.INVALID_PASSWORD_REQUIRED,
      minlength: this.errors.INVALID_PASSWORD_PATTERN,
    },
  };

  protected isAuthenticated = false;
  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _authService: AuthenticationService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _toastService: ToastService,
    private readonly _router: Router
  ) {
    this.signInForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(RegexEnum.email)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  /**
   * Lifecycle hook called after the component is initialized.
   * Checks for automatic login if valid credentials and a non-expired token are present.
   */
  public ngOnInit(): void {
    this._checkAutoLogin();
  }

  protected get f() {
    return this.signInForm.controls;
  }

  /**
   * Handles the "Continue" button click event.
   *
   * This method performs the following actions:
   * 1. **Validates the sign-in form** – Ensures the user has provided valid input.
   * 2. **Clears local storage** – Removes any stored session data before proceeding.
   * 3. **Fetches available businesses** – Calls the authentication service to retrieve the list of businesses associated with the user's email.
   * 4. **Handles business selection logic**:
   *    - If the user has **only one business**, it checks if the new UI is enabled.
   *      - If enabled, proceeds with login.
   *      - If not, displays a restriction modal.
   *    - If the user has **multiple businesses**, redirects to the business selection page.
   * 5. **Handles errors** – Displays appropriate error messages if the login process fails.
   */
  protected onLogin(): void {
    this.isLoading = true;

    // Check if the sign-in form is valid before proceeding
    if (this.signInForm.valid) {
      const { email } = this.signInForm.value;

      // Preserve returnUrl before clearing storage
      const returnUrl = this._localStorageService.readItem(
        LOCAL_STORAGE_KEYS.RETURN_URL
      );
      // Clear existing session data from local storage
      this._localStorageService.clearStorage();

      // Restore returnUrl after clearing
      if (returnUrl) {
        this._localStorageService.storeItem(
          LOCAL_STORAGE_KEYS.RETURN_URL,
          returnUrl
        );
      }
      // Fetch available businesses associated with the provided email
      this._authService
        .getAvailableBusinesses(email)
        .pipe(take(1))
        .subscribe({
          /**
           * Handles the response when available businesses are retrieved.
           * @param businesses - List of businesses associated with the user's email.
           */
          next: (businesses: IUserBusinessInfo[]) => {
            if (businesses.length === 1) {
              // User has only one associated business
              const payload: IUserCredentials = this.signInForm.value;
              payload.businessId = businesses[0].tenantId;

              this._localStorageService.storeItem(
                'tenantId',
                payload.businessId
              );

              // Proceed with login directly
              this._loginToBusiness(payload);
            } else {
              // User has multiple businesses - store and redirect to selection page
              this._localStorageService.storeItem(
                LOCAL_STORAGE_KEYS.AVAILABLE_BUSINESSES,
                businesses
              );
              this._authService.storeCredentials(this.signInForm.value);
              this._router.navigate(['/business-selection']);
            }
          },

          error: (err) => {
            this.isLoading = false;

            if (err.status === 404) {
              // Mark as unauthorized if business is not found
              this.isUnauthorized = true;
              this.errorMessage = this._resolveErrorMessage(err);
            }
            this._toastService.showError('Failed to login');
          },
        });
    } else {
      // Stop loading if the form is invalid
      this.isLoading = false;
    }
  }

  /**
   * Checks if a form control is invalid.
   * Used for showing error messages for specific fields in the form.
   *
   * @param controlName - Name of the form control to validate.
   * @returns `true` if the control is invalid and has been touched; otherwise, `false`.
   */
  protected isInvalid(controlName: string): boolean {
    const control = this.f[controlName];
    return (
      !!control &&
      control.touched &&
      (!!control.errors?.['required'] ||
        control.errors?.['email'] ||
        !!control.errors?.['pattern'] ||
        !!control.errors?.['minlength'])
    );
  }

  /**
   * Toggles the visibility of the password field.
   * Used to show or hide the password input in the form.
   */
  protected togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Handles the event to close the business selection modal.
   */
  protected handleCloseEvent() {
    this.showBusinessSelectionModal = false;
  }

  /**
   * Logs in the user with the provided credentials and business ID.
   *
   * This method:
   * 1. Calls the authentication service to log in with credentials and business ID.
   * 2. If login is successful:
   *    - Clears stored credentials.
   *    - Removes the `availableBusinesses` key from local storage.
   *    - Redirects the user to the appropriate post-login page.
   * 3. If login fails:
   *    - Handles unauthorized access (401).
   *    - Stops loading state.
   *    - Displays an error message.
   *
   * @param payload - The user credentials and business ID required for login.
   */
  private _loginToBusiness(payload: IUserCredentials): void {
    this._authService.loginWithCredsAndBid(payload).subscribe({
      next: (res: IAuthResponse | null) => {
        if (res) {
          // Clear stored authentication credentials
          this._authService.clearCredentials();

          // Remove any stored available businesses as login is successful
          this._localStorageService.removeItem(
            LOCAL_STORAGE_KEYS.AVAILABLE_BUSINESSES
          );

          // Get return URL and redirect
          const returnUrl = this._localStorageService.readItem(
            LOCAL_STORAGE_KEYS.RETURN_URL
          ) as string;
          this._localStorageService.removeItem(LOCAL_STORAGE_KEYS.RETURN_URL);

          // A forced password change takes priority over any pending return
          // URL — otherwise the user gets sent back to the deep link instead
          // of the Change Password screen.
          if (res.changePasswordForced) {
            this._authService.postLoginRedirection(res);
          } else if (returnUrl && returnUrl !== '/') {
            this._router.navigateByUrl(returnUrl);
          } else {
            this._authService.postLoginRedirection(res);
          }
        }
      },

      error: (err) => {
        this.isLoading = false;
        if (err.status === 401) {
          // Mark as unauthorized if credentials are incorrect
          this.isUnauthorized = true;
          this.errorMessage = this._resolveErrorMessage(err);
        }
        // On 403 (e.g. expired password) the auth interceptor already shows
        // the specific error and redirects — avoid a conflicting generic toast.
        if (err.status !== 403) {
          this._toastService.showError(this.toastErrorMessages.LOGIN_FAIL);
        }
      },
    });
  }

  /**
   * Checks for automatic login by verifying stored user credentials and token expiration.
   * Redirects the user to the dashboard if already authenticated.
   */
  private _checkAutoLogin(): void {
    this._handleUnauthorizedAccess();
    const currentUser = this._localStorageService.readItem(
      LOCAL_STORAGE_KEYS.CURRENT_USER
    );
    const tokenExpiration: string | null = this._localStorageService.readItem(
      LOCAL_STORAGE_KEYS.TOKEN_EXPIRATION
    );
    const currentTime = Date.now();

    if (
      currentUser &&
      tokenExpiration &&
      currentTime < Number.parseInt(tokenExpiration, 10)
    ) {
      this.isAuthenticated = true;
      this._router.navigate([ROUTE_CONSTS.INBOX.base]);
    }
  }

  /**
   * Handles unauthorized access by checking the local storage flag.
   * Clears the unauthorized access flag and updates the component state.
   */
  private _handleUnauthorizedAccess(): void {
    const unauthorizedAccess =
      this._localStorageService.readItem('unauthorized_acess');
    this.isUnauthorized = (unauthorizedAccess as boolean) ?? false;
    this.errorMessage = this.errors.INVALID_CREDENTIALS;
    if (unauthorizedAccess) {
      this._localStorageService.removeItem('unauthorized_acess');
    }
  }

  /**
   * Resolves the error message to display for a failed login attempt.
   * Shows the backend message only for the "business not active" case;
   * all other cases fall back to the generic invalid credentials message.
   */
  private _resolveErrorMessage(err: { error?: { message?: string } }): string {
    const backendMessage = err.error?.message;
    return backendMessage === this.errors.BUSINESS_NOT_ACTIVE
      ? backendMessage
      : this.errors.INVALID_CREDENTIALS;
  }
}
